import React from 'react';
import { RadioField, Section, SelectField } from '@dispatch/web/ui';
import { Grid, Divider, Stack, Box } from '@mui/material';
import {
  Actions,
  MultiSelectOption,
  ResourceBulkForm,
  ResourceBulkType,
  Subjects,
} from '@dispatch/shared/types';
import {
  Control,
  FieldValues,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  UseFormGetValues,
} from 'react-hook-form';
import { Ability } from '@casl/ability';
import { CompanyOptions } from '../../BulkUpdateResource';

export type TeamsOfficesRolesProp = {
  errors: FieldErrors;
  control: Control<FieldValues>;
  permissions: Ability<[Actions, Subjects]> | undefined;
  watch: UseFormWatch<ResourceBulkForm>;
  getValues: UseFormGetValues<ResourceBulkForm>;
  companyOptions?: CompanyOptions;
  bulkType?: ResourceBulkType;
  setValues?: UseFormSetValue<ResourceBulkForm>;
};

export const TeamsOfficesRolesBulk = ({
  errors,
  control,
  permissions,
  watch,
  companyOptions,
  bulkType,
  setValues,
  getValues,
}: TeamsOfficesRolesProp) => {
  const roles = companyOptions?.Role as MultiSelectOption[];
  const teams = companyOptions?.Team as MultiSelectOption[];
  const offices = companyOptions?.Office as MultiSelectOption[];
  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');
  const contingentStatus = watch('teamsOfficesRoles.isContingent');
  const isContingent = React.useMemo(() => {
    const contingent = String(contingentStatus) === 'true';
    if (!contingent) {
      setValues?.('teamsOfficesRoles.contingentCompany', '');
    }
    return contingent;
  }, [contingentStatus]);

  return (
    <div
      style={{
        padding: '24px',
      }}
    >
      <Stack>
        <Box
          hidden={
            !hasUpsertPermissions ||
            getValues('isAppendValues.isAppendTeamsOfficesRoles') === 'true'
          }
        >
          <Section>
            <Grid container>
              <Grid item spacing={2} marginBottom={2} xs={4}>
                <RadioField
                  row
                  control={control}
                  id={'isContingent'}
                  name={'teamsOfficesRoles.isContingent'}
                  buttons={[
                    {
                      label: 'Staff',
                      value: false,
                    },
                    {
                      label: 'Contingent',
                      value: true,
                    },
                  ]}
                />
                <Box hidden={!isContingent}>
                  <SelectField
                    id={'contingentCompany'}
                    name={'teamsOfficesRoles.contingentCompany'}
                    label={'Company'}
                    control={control}
                    errors={errors}
                    options={[
                      {
                        title: 'Pilot',
                        value: 'pilot',
                      },
                      {
                        title: 'Crawford',
                        value: 'crawford',
                      },
                      {
                        title: 'Other',
                        value: 'other',
                      },
                    ]}
                    required
                  />
                </Box>
              </Grid>
            </Grid>
          </Section>

          <Divider sx={{ margin: '0.5rem 0 1.375rem 0' }} />
        </Box>

        {bulkType !== 'Team' && (
          <>
            <Section>
              <Grid container>
                <Grid container item spacing={2} marginBottom={2}>
                  <Grid item xs={12} data-test-id="teams-dropdown">
                    <SelectField
                      multiple
                      id="teams"
                      name={'teamsOfficesRoles.teams'}
                      label="Teams"
                      control={control}
                      errors={errors}
                      options={teams || []}
                      readOnly={!hasUpsertPermissions}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Section>
            <Divider sx={{ margin: '0.5rem 0 1.375rem 0' }} />
          </>
        )}

        {bulkType !== 'Office' && (
          <>
            <Section>
              <Grid container>
                <Grid container item spacing={2} marginBottom={2}>
                  <Grid item xs={12} data-test-id="offices-dropdown">
                    <SelectField
                      multiple
                      id="offices"
                      name={'teamsOfficesRoles.offices'}
                      label="Offices"
                      control={control}
                      errors={errors}
                      options={offices || []}
                      readOnly={!hasUpsertPermissions}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Section>
            <Divider sx={{ margin: '0.5rem 0 1.375rem 0' }} />
          </>
        )}
        <Section>
          <Grid container>
            <Grid container item spacing={2} marginBottom={2}>
              <Grid item xs={12} data-test-id="roles-dropdown">
                <SelectField
                  multiple
                  id="roles"
                  name={'teamsOfficesRoles.roles'}
                  label="Roles"
                  control={control}
                  errors={errors}
                  options={roles || []}
                  readOnly={!hasUpsertPermissions}
                />
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Stack>
    </div>
  );
};
